/**
 * Crew insurance and equipment data. Certificates, settings, kits, shift kits and checks are read under RLS; kits
 * and shift kits are edited under RLS by the office; the rostering block by directors. Certificate uploads and
 * job pack checks go through the API.
 */
import { adminBlob, adminFetch, crewFetch } from "../../lib/api";
import { PREVIEW } from "../../lib/preview";
import { supabase } from "../../lib/supabase";

const check = <T>(r: { data: T | null; error: { message: string; code?: string } | null }): T => {
  if (r.error) throw new Error(r.error.code === "42501" ? "Your role cannot change this." : r.error.message);
  return r.data as T;
};
async function me(): Promise<{ org: string; uid: string; role: string }> {
  const { data: s } = await supabase.auth.getSession();
  const uid = s.session?.user.id ?? "";
  const { data, error } = await supabase.from("profiles").select("organisation_id, role").eq("id", uid).single();
  if (error) throw new Error(error.message);
  return { org: data.organisation_id as string, uid, role: data.role as string };
}

/* ---------- insurance ---------- */

export interface Certificate { id: string; crew_member_id: string; insurer: string; policy_number: string | null; cover_amount: number; expires_on: string; file_name: string; uploaded_at: string }

export async function certificates(): Promise<Certificate[]> {
  if (PREVIEW) return [];
  return (check(await supabase.from("crew_insurance_certificates").select("id, crew_member_id, insurer, policy_number, cover_amount, expires_on, file_name, uploaded_at").is("removed_at", null).order("expires_on", { ascending: false })) as Certificate[]).map((c) => ({ ...c, cover_amount: Number(c.cover_amount) }));
}
export function uploadCertificate(crewId: string, f: { insurer: string; policyNumber: string; coverAmount: string; expiresOn: string; file: File }) {
  const form = new FormData();
  form.set("insurer", f.insurer);
  form.set("policyNumber", f.policyNumber);
  form.set("coverAmount", f.coverAmount);
  form.set("expiresOn", f.expiresOn);
  form.set("file", f.file);
  return adminFetch(`/crew/${crewId}/insurance`, { method: "POST", body: form }, { timeoutMs: 60_000 });
}
export const certificateFile = (id: string) => adminBlob(`/insurance/${id}/file`);
export const removeCertificate = (id: string) => adminFetch(`/insurance/${id}/remove`, { method: "POST" });

export async function blockSetting(): Promise<{ block: boolean; director: boolean }> {
  if (PREVIEW) return { block: false, director: true };
  const u = await me();
  const r = check(await supabase.from("crew_insurance_settings").select("block_rostering").maybeSingle()) as { block_rostering: boolean } | null;
  return { block: Boolean(r?.block_rostering), director: u.role === "director" };
}
export async function setBlock(block: boolean) {
  const u = await me();
  check(await supabase.from("crew_insurance_settings").upsert({ organisation_id: u.org, block_rostering: block, updated_by: u.uid }, { onConflict: "organisation_id" }));
}

/** True when an allocation was refused for no current insurance (the database's rostering block). */
export const isInsuranceBlock = (e: unknown) => Boolean(e && typeof e === "object" && ((e as { hint?: string }).hint === "insurance_required" || /No current public liability insurance/.test(String((e as { message?: string }).message))));

/* ---------- kits ---------- */

export interface Kit { id: string; name: string; kind: "ute" | "trailer" | "other"; registration: string | null; notes: string | null; active: boolean; equipment_kit_items: { id: string; name: string; quantity: number; sort_order: number; active: boolean }[] }

export async function kits(): Promise<Kit[]> {
  if (PREVIEW) return [];
  return check(await supabase.from("equipment_kits").select("id, name, kind, registration, notes, active, equipment_kit_items(id, name, quantity, sort_order, active)").order("name")) as Kit[];
}
export async function saveKit(k: { id?: string; name: string; kind: string; registration: string | null; notes: string | null; active: boolean }) {
  const u = await me();
  if (k.id) check(await supabase.from("equipment_kits").update({ name: k.name, kind: k.kind, registration: k.registration, notes: k.notes, active: k.active }).eq("id", k.id));
  else check(await supabase.from("equipment_kits").insert({ organisation_id: u.org, name: k.name, kind: k.kind, registration: k.registration, notes: k.notes }));
}
export async function addKitItem(kitId: string, name: string, quantity: number, sortOrder: number) {
  const u = await me();
  check(await supabase.from("equipment_kit_items").insert({ organisation_id: u.org, kit_id: kitId, name, quantity, sort_order: sortOrder }));
}
export async function removeKitItem(id: string) {
  // Checks keep the item's name, so an item is retired rather than deleted.
  check(await supabase.from("equipment_kit_items").update({ active: false }).eq("id", id));
}

/* ---------- kits on shifts and checks (office) ---------- */

export interface ShiftKitRow { id: string; booking_id: string; kit_id: string }
export interface CheckRow { id: string; booking_id: string; kit_id: string; phase: "before" | "after"; problems: number; notes: string | null; checked_at: string; items: { name: string; status: string; note: string | null; photoKeys: string[] }[] }

export async function shiftKits(bookingIds: string[]): Promise<{ kits: ShiftKitRow[]; checks: CheckRow[] }> {
  if (PREVIEW || !bookingIds.length) return { kits: [], checks: [] };
  const [k, c] = await Promise.all([
    supabase.from("shift_kits").select("id, booking_id, kit_id").in("booking_id", bookingIds),
    supabase.from("equipment_checks").select("id, booking_id, kit_id, phase, problems, notes, checked_at, items").in("booking_id", bookingIds),
  ]);
  return { kits: check(k) as ShiftKitRow[], checks: check(c) as CheckRow[] };
}
export async function assignKit(bookingId: string, kitId: string) {
  const u = await me();
  check(await supabase.from("shift_kits").insert({ organisation_id: u.org, booking_id: bookingId, kit_id: kitId, assigned_by: u.uid }));
}
export async function unassignKit(id: string) {
  check(await supabase.from("shift_kits").delete().eq("id", id));
}
export const checkPhoto = (checkId: string, key: string) => adminBlob(`/equipment-checks/${checkId}/photo?key=${encodeURIComponent(key)}`);

/* ---------- the job pack ---------- */

export interface PackKit { id: string; name: string; kind: string; registration: string | null; items: { id: string; name: string; quantity: number }[] }
export interface PackCheck { id: string; kitId: string; phase: "before" | "after"; problems: number; checkedAt: string; checkedByName: string | null; items: { itemId: string; name: string; status: string; note: string | null; photos: number }[] }
export const packKits = (bookingId: string) => (PREVIEW ? Promise.resolve({ kits: [] as PackKit[], checks: [] as PackCheck[], canCheck: false, kitLabel: "Kit" }) : crewFetch<{ kits: PackKit[]; checks: PackCheck[]; canCheck: boolean; kitLabel: string }>(`/shifts/${bookingId}/kits`));
export function submitCheck(bookingId: string, kitId: string, phase: "before" | "after", items: { itemId: string; status: string; note: string | null }[], photos: Record<string, File[]>, notes: string) {
  const f = new FormData();
  f.set("phase", phase);
  f.set("items", JSON.stringify(items));
  f.set("notes", notes);
  for (const [itemId, files] of Object.entries(photos)) for (const file of files) f.append(`photo-${itemId}`, file);
  return crewFetch<{ check: { id: string; problems: number } }>(`/shifts/${bookingId}/kits/${kitId}/check`, { method: "POST", body: f }, { timeoutMs: 120_000 });
}
